import { css } from 'lit';
import { LOCALE, RelativeDuration } from '@/constants';
import {
	DraftEvent,
	EventStatus,
	LocationTypes,
	ProjectContactDto,
	RoadEventDto,
	UserPermissions,
	WorkZoneRequestStatus,
} from '@typings/api';
import { TableColumns, dateRenderer, dateTitleRenderer } from '@/utils/TableUtils';
import { selectRouteIsExtraneous } from '@/redux/rem/rem-selectors';
import { isLaneImpacted, numberIsValidAndFinite } from '@/utils/utils';
import { TemplateResult, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { memoize } from 'lodash';
import { Districts } from './ConfigCARSx';
import { ConfigREMEventForm } from './ConfigREM';

export const WORK_ZONE_COLUMN_NAMES = <const>[
	'eventType',
	'id',
	'route',
	'mileMarker',
	'direction',
	'county',
	'district',
	'dateCreated',
	'schedule',
	'status',
	'lastUpdated',
	'conformsToLCM',
	'author',
	'detours',
];

export enum WorkZoneStatusFilters {
	ALL = 'All Statuses',
	PUBLISHED = 'Published',
	TO_BE_REVIEWED = 'To Be Reviewed',
	APPROVED = 'Approved',
	REJECTED = 'Rejected',
}

export enum WorkZonePublisherStatusFilters {
	ALL = 'All Statuses',
	TO_BE_PUBLISHED = 'To Be Published',
	PUBLISHED = 'Published',
	REJECTED = 'Rejected',
}

export const ConfigWZRT = {
	table: {
		defaultColumns: [
			'eventType',
			'id',
			'route',
			'mileMarker',
			'direction',
			'county',
			'district',
			'dateCreated',
			'schedule',
			'status',
			'lastUpdated',
			'conformsToLCM',
			'detours',
		] as (typeof WORK_ZONE_COLUMN_NAMES)[number][],
		pagination: [5, 15, 50, 100],
		defaultPagination: 15,
	},
	districtFilters: {
		ALL: {
			label: 'All Districts',
		},
	} as const,
	form: {
		topbarButtonTexts: {
			cancel: 'Cancel',
			create: 'Submit for review',
			update: 'Update',
			updateAndClose: 'Update & Close',
			complete: 'End work zone',
			reopen: 'Re-open work zone',
			approve: 'Approve',
			reject: 'Reject',
		},
		affectedLanes: {
			fieldManualLink: 'https://www.dot.state.mn.us/trafficeng/publ/fieldmanual/index.html',
			districtsForNonStandardLaneClosures: [] as Districts[],
		},
		projectContacts: {
			minimumContacts: 1,
			// these project contact types must also be returned by the /fields endpoint and be set to active in the db, otherwise there will be errors saving the event
			initialProjectContactTypes: ['MNDOT', 'AFTER HOURS'],
			requiredProjectContactTypes: ['MNDOT'],
			showContactType: true,
			tooltip:
				'Use these fields to enter the MnDOT contact in charge of this work zone and any other project contacts.',
		},
		source: {
			defaultSource: 'Work Zone Request',
		},
		scheduling: {
			durationEntryButtons: {
				[RelativeDuration.INDEFINITELY]: {
					userPermissions: [UserPermissions.WZRT_CAN_PUBLISH, UserPermissions.WZRT_CAN_APPROVE],
					label: 'Indefinitely',
				},
				[RelativeDuration.HOUR]: {
					userPermissions: [
						UserPermissions.WZRT_CAN_CREATE,
						UserPermissions.WZRT_CAN_PUBLISH,
						UserPermissions.WZRT_CAN_APPROVE,
					],
					label: 'One Hour',
				},
				[RelativeDuration.TODAY]: {
					userPermissions: [
						UserPermissions.WZRT_CAN_CREATE,
						UserPermissions.WZRT_CAN_PUBLISH,
						UserPermissions.WZRT_CAN_APPROVE,
					],
					label: 'Today',
				},
				[RelativeDuration.DAY]: {
					userPermissions: [
						UserPermissions.WZRT_CAN_CREATE,
						UserPermissions.WZRT_CAN_PUBLISH,
						UserPermissions.WZRT_CAN_APPROVE,
					],
					label: 'One Day',
				},
				[RelativeDuration.WEEK]: {
					userPermissions: [
						UserPermissions.WZRT_CAN_CREATE,
						UserPermissions.WZRT_CAN_PUBLISH,
						UserPermissions.WZRT_CAN_APPROVE,
					],
					label: 'One Week',
				},
			},
		},
		allowedLocationTypes: [LocationTypes.MAJOR_ROAD, LocationTypes.RAMP],
	},
	statusActions: {
		APPROVE: {
			userPermission: UserPermissions.WZRT_CAN_APPROVE,
			successMessage: 'Succesfully approved work zone request',
			errorMessage: 'Error attempting to approve work zone request',
			compatiableStatuses: [
				WorkZoneRequestStatus.TO_BE_REVIEWED_NEW,
				WorkZoneRequestStatus.TO_BE_REVIEWED_EDIT,
			],
		},
		REJECT: {
			userPermission: UserPermissions.WZRT_CAN_REJECT,
			successMessage: 'Succesfully rejected work zone request',
			errorMessage: 'Error attempting to reject work zone request',
			compatiableStatuses: [
				WorkZoneRequestStatus.TO_BE_REVIEWED_NEW,
				WorkZoneRequestStatus.TO_BE_REVIEWED_EDIT,
				WorkZoneRequestStatus.TO_BE_PUBLISHED_NEW,
				WorkZoneRequestStatus.TO_BE_PUBLISHED_EDIT,
			],
		},
		PUBLISH: {
			userPermission: UserPermissions.WZRT_CAN_PUBLISH,
			successMessage: 'Succesfully published work zone request',
			errorMessage: 'Error attempting to publish work zone request',
			compatiableStatuses: [
				WorkZoneRequestStatus.TO_BE_PUBLISHED_NEW,
				WorkZoneRequestStatus.TO_BE_PUBLISHED_EDIT,
			],
		},
		UNPUBLISH: {
			successMessage: 'Succesfully unpublished work zone request',
			errorMessage: 'Error attempting to unpublish work zone request',
			compatiableStatuses: [WorkZoneRequestStatus.PUBLISHED],
		},
		UNAPPROVE: {
			successMessage: 'Succesfully unapproved work zone request',
			errorMessage: 'Error attempting to unapprove work zone request',
			compatiableStatuses: [
				WorkZoneRequestStatus.TO_BE_PUBLISHED_NEW,
				WorkZoneRequestStatus.TO_BE_PUBLISHED_EDIT,
			],
		},
	},
	login: {
		title: 'Welcome to the new <br /> Minnesota DOT Work Zone Request Tool',
		descriptionsAbove: ['To enter a new work zone request, please validate your email below.'],
		descriptionsBelow: [
			'For guidance on entering work zone requests, please click the green button above to access training materials.',
			'If you need further assistance, please contact us using the information below: <br /> Metro Work Zone Requests: 651-234-7110 <br /> Greater Minnesota Work Zone Requests: 651-234-7089',
		],
	},
	trainingMaterials: {
		label: 'WZRT Help Guide',
	},
};

export const RequiredFieldsByLocationType = {
	[LocationTypes.MAJOR_ROAD]: [
		'eventType',
		'eventSource',
		'route',
		'startMileMarker',
		'positiveLaneBlockage',
		'negativeLaneBlockage',
		'dateStart',
		'dateEnd',
		'projectContacts',
		'minRequiredQuantities',
	] as Array<keyof RoadEventDto>,
	[LocationTypes.RAMP]: [
		'eventType',
		'eventSource',
		'route',
		'rampPk',
		'dateStart',
		'dateEnd',
		'projectContacts',
		'minRequiredQuantities',
	] as Array<keyof RoadEventDto>,
	[LocationTypes.LOCAL_ROAD]: [
		'eventType',
		'eventSource',
		'route',
		'dateStart',
		'dateEnd',
		'projectContacts',
		'minRequiredQuantities',
	] as Array<keyof RoadEventDto>,
	[LocationTypes.AREA]: [] as Array<keyof RoadEventDto>,
	[LocationTypes.GPS]: [] as Array<keyof RoadEventDto>,
};

const EMPTY_PROJECT_CONTACT: ProjectContactDto = {
	name: '',
	phoneNumber: '',
};

export const getEmptyWorkZoneRequest = (): DraftEvent => ({
	eventType: undefined,
	route: undefined,
	startMileMarker: undefined,
	dateStart: undefined,
	dateEnd: undefined,
	eventSource: ConfigWZRT.form.source.defaultSource,
	eventLocation: LocationTypes.MAJOR_ROAD,
	priorityLevel: 5,
	projectContacts: ConfigWZRT.form.projectContacts.initialProjectContactTypes.map(
		(contactType) => ({ ...EMPTY_PROJECT_CONTACT, contactType }),
	),
	emailEnabled: true,
});

export const WZRT_TABLE_COLUMNS: TableColumns<
	(typeof WORK_ZONE_COLUMN_NAMES)[number],
	RoadEventDto
> = {
	eventType: {
		label: 'Event type',
		width: '3fr',
		columnFiltering: true,
		valueGetter: (event: RoadEventDto) => event.eventType,
		renderer: (event: RoadEventDto): TemplateResult => html`
			<span class="flex align-items-center gap-half">
				<rem-event-icon
					eventId=${event.id}
					class="${classMap({
						closed: event.eventStatus === EventStatus.COMPLETED,
					})}"
				></rem-event-icon>

				${event.eventType}
			</span>
		`,
	},
	conformsToLCM: {
		label: 'Conforms to LCM',
		width: '1.5fr',
		valueGetter: (event: RoadEventDto) => {
			const showValue = (event.locationDetails?.district ?? []).some((districtName) =>
				ConfigWZRT.form.affectedLanes.districtsForNonStandardLaneClosures.includes(
					districtName as Districts,
				),
			);

			if (!showValue) {
				return '--';
			}

			// slightly confusing naming, if an event has non standard lane closures, it does NOT conform to the lane closure guidelines
			return event.hasNonStandardLaneClosures ? 'No' : 'Yes';
		},
	},
	id: {
		label: 'Id',
		width: '1fr',
		valueGetter: (event: RoadEventDto) => event.id,
	},
	route: {
		label: 'Route',
		width: '1fr',
		columnFiltering: true,
		valueGetter: (event: RoadEventDto) => (selectRouteIsExtraneous(event) ? '--' : event.route),
	},
	mileMarker: {
		label: 'Mile marker',
		width: '1fr',
		minWidth: 'calc(2ch + var(--ggutter) * 2)',
		valueGetter(event: RoadEventDto) {
			if (typeof event.startMileMarker === 'number' && typeof event.endMileMarker !== 'number') {
				return `${event.startMileMarker.toFixed(1)}`; //	just start marker
			}
			if (typeof event.startMileMarker === 'number' && typeof event.endMileMarker === 'number') {
				return `${event.startMileMarker.toFixed(1)} - ${event.endMileMarker.toFixed(1)}`; //	start and end markers
			}
			return '--'; //	no marker defined
		},
	},
	direction: {
		label: 'Direction',
		width: '1fr',
		valueGetter(event: RoadEventDto) {
			const directionArray: string[] = [];
			const negativeDirectionIsImpacted = isLaneImpacted(event.negativeLaneBlockage);
			const positiveDirectionIsImpacted = isLaneImpacted(event.positiveLaneBlockage);

			if (negativeDirectionIsImpacted && event?.locationDetails?.lanes?.negative?.cardinalDir) {
				directionArray.push(event.locationDetails.lanes.negative.cardinalDir);
			}
			if (positiveDirectionIsImpacted && event?.locationDetails?.lanes?.positive?.cardinalDir) {
				directionArray.push(event.locationDetails.lanes.positive.cardinalDir);
			}
			return directionArray.length > 0 ? directionArray.join(', ') : '--';
		},
	},
	county: {
		label: 'County',
		width: '1fr',
		columnFiltering: true,
		valueGetter: (event: RoadEventDto) =>
			event?.locationDetails?.county?.length ? event?.locationDetails?.county : '--',
	},
	district: {
		label: 'District',
		width: '1fr',
		columnFiltering: true,
		valueGetter: (event: RoadEventDto) =>
			event?.locationDetails?.district?.length ? event?.locationDetails?.district : '--',
		doNotPrint: true,
	},
	dateCreated: {
		label: 'Date Created',
		width: '3fr',
		valueGetter: (event: RoadEventDto) => event.created,
		titleRenderer: memoize(
			(event: RoadEventDto) => {
				return dateTitleRenderer(event.created);
			},
			(event) => event.created,
		),
		renderer: memoize(
			(event: RoadEventDto) => {
				return dateRenderer(event.created);
			},
			(event) => event.created,
		),
		isDate: true,
	},
	schedule: {
		label: 'Schedule',
		width: '3fr',
		valueGetter: (event: RoadEventDto) => event.dateStart,
		renderer: memoize(
			(event: RoadEventDto) => {
				if (typeof event.dateStart !== 'number' || typeof event.dateEnd !== 'number') {
					return `--`;
				}
				return `${new Date(event.dateStart).toLocaleString(
					LOCALE,
					ConfigREMEventForm.wholeDatestampFormatOptions,
				)}
					${
						numberIsValidAndFinite(event.dateEnd)
							? ` -
							${new Date(event.dateEnd).toLocaleString(LOCALE, ConfigREMEventForm.wholeDatestampFormatOptions)}`
							: ``
					}`;
			},
			(event) => event.dateStart,
		),
		isDate: true,
		doNotPrint: true,
	},
	status: {
		label: 'Status',
		width: '2fr',
		renderer: (event: RoadEventDto) =>
			html`<work-zone-status-badge .publishStatus=${event.publishStatus}></work-zone-status-badge>`,
		valueGetter: (event: RoadEventDto) => event.publishStatus,
	},
	lastUpdated: {
		label: 'Last Updated',
		width: '2fr',
		valueGetter: (event: RoadEventDto) => event.updated,
		titleRenderer: memoize(
			(event: RoadEventDto) => {
				return dateTitleRenderer(event.updated);
			},
			(event) => event.updated,
		),
		renderer: memoize(
			(event: RoadEventDto) => {
				return dateRenderer(event.updated);
			},
			(event) => event.updated,
		),
		isDate: true,
		doNotPrint: true,
	},
	detours: {
		label: 'Detours',
		width: '1fr',
		columnFiltering: true,
		valueGetter: (event: RoadEventDto): string => (event.detours?.length ? 'Yes' : 'No'),
		renderer: (event: RoadEventDto): TemplateResult =>
			event.detours?.length
				? html`
						<span class="flex align-items-center justify-content-center"
							><svg role="presentation" class="checkmark">
								<use href="img/sprites.svg#icon-checkmark"></use>
							</svg>
						</span>
					`
				: html`<span class="flex justify-content-center">--</span>`,
		columnFilterRenderer: (event: RoadEventDto): TemplateResult =>
			event.detours?.length
				? html`
						<span class="flex align-items-center justify-content-center gap-half"
							>Detour
							<svg role="presentation" class="checkmark">
								<use href="img/sprites.svg#icon-checkmark"></use>
							</svg>
						</span>
					`
				: html`<span class="flex justify-content-center">No detour</span>`,
	},
	author: {
		label: 'Author',
		width: '2fr',
		columnFiltering: true,
		valueGetter: (event: RoadEventDto) => event.createdBy,
	},
};

export const WORK_ZONE_TABLE_STATE_KEY = 'work-zone-table';
